import { useState } from 'react';
import { Settings, Bell, Monitor, Moon, MailCheck } from 'lucide-react';
import Modal from '../../../../components/modals/Modal';
import Button from '../../../../components/ui/Button';

function ToggleRow({ icon: Icon, label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-brand-gray-light/50 transition-colors">
      <div className="flex items-center space-x-3">
        <div className="w-9 h-9 rounded-lg bg-brand-navy/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-brand-navy" />
        </div>
        <div>
          <p className="text-[15px] font-semibold text-brand-navy">{label}</p>
          <p className="text-xs text-brand-navy/50">{description}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 ${
          checked ? 'bg-brand-gold' : 'bg-gray-300'
        }`}
        aria-checked={checked}
        role="switch"
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

export default function SettingsModal({ isOpen, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [prefs, setPrefs] = useState({
    emailNotifications: true,
    pushNotifications: false,
    darkMode: false,
    compactView: false,
  });

  const handleToggle = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
    setSaved(false);
  };

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSaved(true);
    }, 800);
  };

  const handleClose = () => {
    onClose();
    setSaved(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="User Settings" icon={Settings} maxWidth="max-w-lg">
      <div className="space-y-2">
        <ToggleRow
          icon={MailCheck}
          label="Email Notifications"
          description="Receive updates via email"
          checked={prefs.emailNotifications}
          onChange={() => handleToggle('emailNotifications')}
        />
        <ToggleRow
          icon={Bell}
          label="Push Notifications"
          description="Browser and desktop alerts"
          checked={prefs.pushNotifications}
          onChange={() => handleToggle('pushNotifications')}
        />
        <ToggleRow
          icon={Moon}
          label="Dark Mode"
          description="Switch to dark theme"
          checked={prefs.darkMode}
          onChange={() => handleToggle('darkMode')}
        />
        <ToggleRow
          icon={Monitor}
          label="Compact View"
          description="Reduce spacing and card sizes"
          checked={prefs.compactView}
          onChange={() => handleToggle('compactView')}
        />
      </div>

      {saved && (
        <div className="mt-5 p-3 bg-green-50 border border-green-200 rounded-lg text-[15px] text-green-700 text-center">
          Settings saved successfully.
        </div>
      )}

      <div className="flex space-x-3 pt-5">
        <Button type="button" variant="secondary" className="flex-1" onClick={handleClose}>
          Cancel
        </Button>
        <Button type="button" variant="primary" className="flex-1" loading={loading} onClick={handleSave}>
          Save Settings
        </Button>
      </div>
    </Modal>
  );
}
